import React, { FC, MouseEvent, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import PublicIcon from '@material-ui/icons/Public';
import SnoozeIcon from '@material-ui/icons/Snooze';
import AlarmOnIcon from '@material-ui/icons/AlarmOn';

import { createOrUpdateGame } from '../games/apiMutations';
import { Game } from '../../utils/types';
import { convertGameToGameInput } from '../../utils/inputTransform';

const GOOGLE_URL_STEM = 'https://www.google.com/search?q=';

const ActionMenu: FC<ActionMenuProps> = (props) => {
	const [updateGame] = useMutation(createOrUpdateGame);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const [dialogOpen, setDialogOpen] = useState<string>('');
	const [gameExclude, setGameExclude] = useState<boolean | null>(null);

	const { game, reloadGames } = props;
	if (!game) return null;
	const { airings, excludeScheduling, title } = game;

	const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
		setAnchorEl(event.currentTarget);
	};
	const open = Boolean(anchorEl);

	const startChallenge = (game: Game, exclude: boolean): void => {
		setDialogOpen(game.id);
		setGameExclude(exclude);
	};

	const updateGameStatus = async (): Promise<void> => {
		const gameInput = convertGameToGameInput(game);
		gameInput.excludeScheduling = !!gameExclude;

		await updateGame({ variables: { game: gameInput } });

		reloadGames();
		cancelChallenge();
	};

	const cancelChallenge = (): void => {
		setAnchorEl(null);
		setDialogOpen('');
		setGameExclude(null);
	};

	const googleLinkDisabled = !airings || airings?.items?.length === 0;
	const isExcludeScheduling = excludeScheduling === true;

	const handleClose = (): void => {
		setAnchorEl(null);
	};

	const dialogIconStyle = { marginRight: '1rem', verticalAlign: 'middle' };

	const renderDialog = (airingId: string): JSX.Element => {
		return (
			<Dialog
				open={dialogOpen === airingId}
				onClose={cancelChallenge}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'>
				<DialogTitle id='alert-dialog-title'>
					{gameExclude && (
						<>
							<SnoozeIcon style={dialogIconStyle} />
							Exclude from auto-scheduling
						</>
					)}
					{!gameExclude && (
						<>
							<AlarmOnIcon style={dialogIconStyle} />
							Re-add to auto-scheduling
						</>
					)}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id='alert-dialog-description'>
						Are you sure you want to {gameExclude ? 'exclude' : 're-add'} game '<strong>{game.title}</strong>' {gameExclude ? 'from' : 'to'} Airing
						auto-scheduling?
					</DialogContentText>{' '}
					<DialogContentText id='alert-dialog-description-2'>
						Games which are <strong>excluded</strong> from auto-scheduling may <strong>miss late scheduling adjustments</strong> and this feature
						should be used sparingly.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelChallenge} color='primary'>
						Cancel
					</Button>
					<Button onClick={updateGameStatus} color='primary' autoFocus>
						I Understand - Proceed
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	const menuIconStyle = { marginRight: '-1rem', verticalAlign: 'middle' };

	return (
		<>
			<IconButton
				id='action-menu-button'
				aria-controls={open ? 'action-menu-menu' : undefined}
				aria-haspopup='true'
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
				size='small'>
				<MenuIcon />
			</IconButton>
			<Menu
				id='action-menu-menu'
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					'aria-labelledby': 'action-menu-button'
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
				<MenuItem
					onClick={(): Window | null => {
						handleClose();
						return window.open(GOOGLE_URL_STEM + title, '_blank');
					}}
					disabled={googleLinkDisabled}>
					<ListItemIcon style={menuIconStyle}>
						<PublicIcon />
					</ListItemIcon>
					<ListItemText>
						Try it on <strong>Google</strong>
					</ListItemText>
				</MenuItem>
				<Divider />
				{!isExcludeScheduling && (
					<MenuItem onClick={(): void => startChallenge(game, true)}>
						<ListItemIcon style={menuIconStyle}>
							<SnoozeIcon />
						</ListItemIcon>
						<ListItemText>
							<strong>Exclude</strong> from auto-scheduling
						</ListItemText>
					</MenuItem>
				)}
				{isExcludeScheduling && (
					<MenuItem onClick={(): void => startChallenge(game, false)}>
						<ListItemIcon style={menuIconStyle}>
							<AlarmOnIcon />
						</ListItemIcon>
						<ListItemText>
							<strong>Re-add</strong> to auto-scheduling
						</ListItemText>
					</MenuItem>
				)}
			</Menu>
			{renderDialog(game.id)}
		</>
	);
};

type ActionMenuProps = {
	game: Game;
	reloadGames: Function;
};

export default ActionMenu;
